const LOCAL_BUSINESS = {
  "@context": "https://schema.org",
  "@type": "HomeAndConstructionBusiness",
  name: "Dépan'Volets",
  telephone: "+33374473240",
  email: "contact@depanvolets-strasbourg.fr",
  url: "https://depanvolets-strasbourg.fr",
  priceRange: "€€",
  description:
    "Réparation, dépannage, motorisation et pose de volets roulants à Strasbourg et dans le Bas-Rhin. Intervention rapide, devis gratuit, toutes marques.",
  address: {
    "@type": "PostalAddress",
    streetAddress: "82 Rue de Heidenberg",
    addressLocality: "Strasbourg",
    postalCode: "67200",
    addressCountry: "FR",
    addressRegion: "Grand Est",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: "48.573405",
    longitude: "7.752111",
  },
  areaServed: {
    "@type": "Place",
    name: "Bas-Rhin",
  },
  image: "https://depanvolets-strasbourg.fr/og/og-default.jpg",
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:00",
      closes: "19:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: "Saturday",
      opens: "09:00",
      closes: "17:00",
    },
  ],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+33374473240",
    contactType: "customer service",
    availableLanguage: "French",
    areaServed: "FR",
  },
  hasMap:
    "https://www.google.com/maps/search/?api=1&query=82+Rue+de+Heidenberg,+67200+Strasbourg,+France",
  sameAs: [
    "https://www.google.com/search?q=Dépan%27Volets+Strasbourg",
  ],
};

function getDefaultOgImage(path: string): string {
  const seq = path === "/" ? "og-home" : `og-${path.replace(/\//g, "").replace(/-/g, "-")}`;
  const prompt = encodeURIComponent(
    `Professional roller shutter repair technician in a blue uniform working on a white roller shutter in a modern French house in Strasbourg Alsace, clean daylight, warm tones, professional photography, social media banner, 1200x630, minimal text space, ${path === "/" ? "home page" : "service page"}`
  );
  return `https://readdy.ai/api/search-image?query=$%7Bprompt%7D&width=1200&height=630&seq=${seq}&orientation=landscape`;
}

interface SEOHeadProps {
  title: string;
  description: string;
  path?: string;
  ogImage?: string;
  ogTitle?: string;
  ogDescription?: string;
  type?: "website" | "article" | "service";
  robots?: string;
  faqs?: Array<{ question: string; answer: string }>;
  serviceType?: string;
  city?: string;
  breadcrumbs?: Array<{ label: string; href?: string }>;
}

export default function SEOHead({
  title,
  description,
  path,
  ogImage,
  ogTitle,
  ogDescription,
  type = "website",
  robots = "index, follow",
  faqs,
  serviceType,
  city,
  breadcrumbs,
}: SEOHeadProps) {
  const canonicalUrl = path ? `https://depanvolets-strasbourg.fr${path}` : "https://depanvolets-strasbourg.fr";
  const ogUrl = canonicalUrl;
  const ogImageUrl = ogImage || getDefaultOgImage(path || "/");
  const displayOgTitle = ogTitle || title;
  const displayOgDescription = ogDescription || description;
  const lastModified = new Date().toUTCString();

  const schemas: Array<Record<string, unknown>> = [];

  // LocalBusiness
  const localBusiness = {
    ...LOCAL_BUSINESS,
    address: {
      ...LOCAL_BUSINESS.address,
      ...(city && city !== "Strasbourg"
        ? { addressLocality: city, addressRegion: "Grand Est" }
        : {}),
    },
    areaServed: city
      ? { "@type": "Place", name: city }
      : LOCAL_BUSINESS.areaServed,
  };
  schemas.push(localBusiness);

  // Service
  if (serviceType) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType,
      provider: {
        "@type": "HomeAndConstructionBusiness",
        name: "Dépan'Volets",
        telephone: "+33374473240",
        url: "https://depanvolets-strasbourg.fr",
      },
      areaServed: city
        ? { "@type": "Place", name: city }
        : { "@type": "Place", name: "Bas-Rhin" },
      priceRange: "€€",
    });
  }

  // FAQPage
  if (faqs && faqs.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    });
  }

  // BreadcrumbList
  if (breadcrumbs && breadcrumbs.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.label,
        ...(item.href ? { item: `https://depanvolets-strasbourg.fr${item.href}` } : {}),
      })),
    });
  }

  // WebPage
  schemas.push({
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description,
    url: canonicalUrl,
    inLanguage: "fr-FR",
    isPartOf: {
      "@type": "WebSite",
      name: "Dépan'Volets",
      url: "https://depanvolets-strasbourg.fr",
    },
  });

  return (
    <>
      <meta charSet="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="robots" content={robots} />
      <meta name="description" content={description} />
      <meta name="last-modified" content={lastModified} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="geo.position" content="48.573405;7.752111" />
      <meta name="geo.region" content="FR-67" />
      <meta name="geo.placename" content={city || "Strasbourg"} />
      <meta name="language" content="French" />
      <link rel="alternate" hrefLang="fr-FR" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={displayOgTitle} />
      <meta property="og:description" content={displayOgDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="fr_FR" />
      <meta property="og:site_name" content="Dépan'Volets" />
      <meta property="og:locale:alternate" content="fr_FR" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={displayOgTitle} />
      <meta name="twitter:description" content={displayOgDescription} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:site" content="@DepanVolets" />

      {/* JSON-LD */}
      {schemas.map((schema, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </>
  );
}